/**
 * People — the roster, who may teach, and when reminders go out.
 *
 * **No emails.** A teacher gets a name, a handle and a team. Addresses appear
 * in a course's report and its CSV, to that course's teacher-owner and to
 * admins, and nowhere else — the roster is the one screen where "everybody in
 * the company" and "everybody's contact details" would otherwise be the same
 * list.
 *
 * Teaching is granted by an admin and by nobody else, so a teacher who is not
 * an admin sees this page read-only rather than seeing controls that refuse.
 * The grants are stated whole on every change: `PUT /org/teachers` takes the
 * list, and removing somebody is sending it without them.
 */

import type { AssignScope } from '@wire';
import { org, teach } from '../api';
import { Avatar } from '../components/Bits';
import { ErrorNote, Footnote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { ds } from '../ds';
import { useApp } from '../state';
import { HAIR, meta, monoPlain, plural } from '../theme';
import { useActions, useLoad } from '../useLoad';

/** The offsets an admin can choose between, in days before the deadline. */
const OFFSETS = [14, 7, 3, 1, 0];

export default function People() {
  const { Switch } = ds();
  const { me } = useApp();
  const admin = me?.role === 'admin';

  const { data, error, loading, reload } = useLoad(() => teach.people(), 'Could not load people.');
  const grants = useLoad(
    () => (admin ? org.teachers() : Promise.resolve(null)),
    'Could not load who may teach.',
    admin ? 'admin' : 'member'
  );
  const { busy, error: wrote, ok, act } = useActions(async () => {
    await Promise.all([reload(), grants.reload()]);
  });

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const scopes: AssignScope[] = (grants.data?.grants ?? []).map((g) => g.scope);
  const has = (scope: AssignScope) => scopes.includes(scope);

  const toggle = (scope: AssignScope, on: boolean, who: string) =>
    void act(
      () => org.setTeachers(on ? [...scopes, scope] : scopes.filter((s) => s !== scope)),
      on ? `${who} can teach.` : `${who} can no longer teach.`
    );

  const setOffsets = (next: number[]) =>
    void act(
      () => org.setReminders({ offsets: next.sort((a, b) => b - a) }),
      'The reminder schedule is saved.'
    );

  return (
    <>
      <PageTitle
        title="People."
        meta={meta(
          plural(data.members.length, 'person', 'people'),
          plural(data.roles.length, 'team')
        )}
      />

      <ErrorNote>{wrote ?? grants.error}</ErrorNote>
      <OkNote>{ok}</OkNote>

      <Panel style={{ display: 'grid', gap: 10 }}>
        <PanelHead
          title="Teams"
          meta={admin ? 'Everyone in a team can teach when it is switched on' : null}
        />

        {data.roles.map((role) => (
          <div
            key={role.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 0',
              borderTop: HAIR
            }}
          >
            <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text-strong)' }}>
              {role.name}
            </span>
            <span style={monoPlain}>{plural(role.members, 'person', 'people')}</span>
            {admin && (
              <Switch
                label="Can teach"
                checked={has(`role:${role.id}`)}
                disabled={busy}
                onChange={(next) => toggle(`role:${role.id}`, next, role.name)}
              />
            )}
          </div>
        ))}

        {data.roles.length === 0 && (
          <Footnote>Teams come from the enterprise console.</Footnote>
        )}
      </Panel>

      <Panel style={{ display: 'grid', gap: 10 }}>
        <PanelHead title="Roster" meta={plural(data.members.length, 'person', 'people')} />

        {data.members.map((person) => (
          <div
            key={person.userId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 0',
              borderTop: HAIR
            }}
          >
            <Avatar name={person.name} handle={person.handle} />
            <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
              <span style={{ fontSize: 14, color: 'var(--text-strong)' }}>{person.name}</span>
              <span style={monoPlain}>
                {meta(person.handle ? `@${person.handle}` : null, person.team, person.isAdmin ? 'admin' : null)}
              </span>
            </span>

            {admin &&
              (person.isAdmin ? (
                // An admin teaches without a grant, so there is nothing here to
                // switch off — and a disabled switch reading "on" would suggest
                // there is.
                <span style={monoPlain}>Teaches as an admin</span>
              ) : (
                <Switch
                  label="Can teach"
                  checked={has(person.userId as AssignScope)}
                  disabled={busy}
                  onChange={(next) => toggle(person.userId as AssignScope, next, person.name)}
                />
              ))}
            {!admin && person.canTeach && <span style={monoPlain}>Teaches</span>}
          </div>
        ))}
      </Panel>

      {admin && (
        <Panel style={{ display: 'grid', gap: 14 }}>
          <PanelHead title="Reminders" />

          <Switch
            label="Remind people about deadlines"
            checked={data.reminders.remind}
            disabled={busy}
            onChange={(next) =>
              void act(
                () => org.setReminders({ remind: next }),
                next ? 'Reminders are on.' : 'Reminders are off.'
              )
            }
          />

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {OFFSETS.map((days) => {
              const on = data.reminders.offsets.includes(days);
              return (
                <button
                  key={days}
                  type="button"
                  aria-pressed={on}
                  disabled={busy || !data.reminders.remind}
                  onClick={() =>
                    setOffsets(
                      on
                        ? data.reminders.offsets.filter((d) => d !== days)
                        : [...data.reminders.offsets, days]
                    )
                  }
                  style={{
                    padding: '7px 13px',
                    borderRadius: 999,
                    border: on ? '1px solid transparent' : HAIR,
                    background: on ? 'var(--primary-soft)' : 'var(--surface-card)',
                    color: on ? 'var(--primary-text)' : 'var(--text-body)',
                    fontSize: 13
                  }}
                >
                  {days === 0 ? 'On the day' : days === 1 ? '1 day before' : `${days} days before`}
                </button>
              );
            })}
          </div>

          <Footnote>Every assignment follows this schedule.</Footnote>
        </Panel>
      )}
    </>
  );
}
